import React, { useMemo, useState } from "react";
import {
  Col,
  Row,
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Dropzone from 'react-dropzone';
import __ from "utils/i18n";
import { fetchResource } from "api-provider";
import { validateCsv } from "../resources";
import Icon from '@mdi/react';
import { mdiCloudUploadOutline } from '@mdi/js';
import { backendUrl, tenantId } from 'config/envConfig';

const ImportStep = (props) => {
  const {
    navigation,
    hideModal,
    subsidiaryId,
    token,
    isFetching,
    setIsFetching,
    setUploadOk,
    setResults,
    setFileName
  } = props;

  const [localError, setLocalError] = useState(null);

  const authToken = useMemo(() => {
    if (typeof window === 'undefined') {
      return token;
    }

    try {
      const storedToken = window.localStorage.getItem(`geop_oauth_${tenantId}`);
      if (!storedToken) {
        return token;
      }

      const parsed = JSON.parse(storedToken);
      if (parsed?.token_type && parsed?.access_token) {
        return `${parsed.token_type} ${parsed.access_token}`;
      }

      return token;
    } catch (error) {
      console.warn('Unable to read oauth token from storage', error);
      return token;
    }
  }, [token]);

  const downloadFile = async () => {
    try {
      const response = await fetch(
        `${backendUrl}/api/v1/email-ecommerce-manager/campaigns/template`,
        {
          method: 'GET',
          headers: {
            'x-client-id': tenantId,
            'X-Authentication-Token': authToken,
            Authorization: authToken,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Error al descargar: ${response.status} ${response.statusText}`.trim());
      }

      const contentDisposition = response.headers.get('content-disposition');
      let fileName = `massive_payment_links_${Date.now()}.csv`;
      
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error('El archivo descargado está vacío');
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error al descargar la plantilla:', error);
      alert(`Error al descargar la plantilla: ${error.message}. Por favor, intente nuevamente.`);
    }
  };

  const isCsvFile = (file) => {
    if (!file) return false;
    const name = (file.name || '').toLowerCase();
    const type = (file.type || '').toLowerCase();
    const byExtension = name.endsWith('.csv');
    const byMime = type === 'text/csv' || type === 'application/vnd.ms-excel' || type === 'application/csv' || type === 'text/plain';
    return byExtension || byMime;
  };

  const onUpload = async (file) => {
    setIsFetching(true);
    setFileName(file.name); 
    navigation.go(1); 
    
    try {
      if (!subsidiaryId || Number(subsidiaryId) <= 0) {
        throw new Error('Debe seleccionar una sucursal antes de importar.');
      }

      const request = fetchResource(validateCsv(file, authToken, subsidiaryId));
      const result = await new Promise((resolve, reject) => {
        request
          .onSuccess((data) => resolve(data))
          .onFailure((err) => reject(err));
      });

      setIsFetching(false);
      
      const payload = result?.data ?? result;
      const validCount = payload?.valid_count ?? payload?.validCount ?? 0;
      const invalidCount = payload?.invalid_count ?? payload?.invalidCount ?? 0;
      const normalizedData = {
        campaignUuid: payload?.campaign_uuid || payload?.campaignUuid || payload?.campaign_id || payload?.campaignId,
        totalProcessed: validCount + invalidCount,
        successful: validCount,
        failed: invalidCount,
        hash: payload?.hash,
        estimatedLinks: payload?.estimated_links ?? payload?.estimatedLinks,
        raw: payload,
      };

      const rawLineErrors = payload?.line_errors ?? payload?.lineErrors ?? [];
      const normalizedErrors = Array.isArray(rawLineErrors)
        ? rawLineErrors.map((err, index) => {
            const lineNumber = err?.line_number ?? err?.lineNumber ?? index + 1;
            const preferredMessage =
              err?.message ||
              err?.error ||
              (err?.code ? `${err.code}${err?.column ? ` (${err.column})` : ''}` : null) ||
              __('dashboard.sections.massivePaymentLink.import.upload.error');
            return {
              row: lineNumber,
              message: preferredMessage,
              item: {
                title: err?.column,
                email: err?.value,
              },
            };
          })
        : [];

      const generalErrors = payload?.errors ?? payload?.error ?? [];
      const generalMessages = Array.isArray(payload?.errors_messages)
        ? payload.errors_messages
        : Array.isArray(generalErrors)
          ? generalErrors
          : [];
      if (generalMessages.length > 0) {
        generalMessages.forEach((msg) => {
          normalizedErrors.push({
            row: '-',
            message: msg,
          });
        });
      }

      const isValid = payload?.is_valid ?? payload?.isValid ?? false;

      if (isValid) {
        setUploadOk(true);
        setResults(normalizedData);
        navigation.go(2);
      } else {
        setUploadOk(false);
        setResults({
          data: normalizedData,
          errors: normalizedErrors.length > 0 ? normalizedErrors : null,
          errorType: payload?.error_type ?? payload?.errorType ?? null,
          message:
            (Array.isArray(generalErrors) && generalErrors.length > 0 && generalErrors.join(', ')) ||
            payload?.message ||
            null,
        });
        navigation.go(2);
      }
    } catch (error) {
      setIsFetching(false);
      setUploadOk(false);
      setResults({
        data: null,
        errors: [{ error: error.message || "Error al subir el archivo" }]
      });
      navigation.go(2); // Go to results step
    }
  };

  return (
    <div className="import-modal">
      <ModalHeader toggle={hideModal}>
        {__('dashboard.sections.massivePaymentLink.import.title')}
      </ModalHeader>

      <ModalBody>
        <Row>
          <Col md='12'>
            <h4 className='import-modal__subtitle'>
              {__('dashboard.sections.massivePaymentLink.import.template.title')}
            </h4>
            <div className="block-content import-block-content">
              <p>
                {__.bb(
                  'dashboard.sections.massivePaymentLink.import.template.message'
                )}
              </p>

              <Button color='secondary' size='md' onClick={downloadFile}>
                <i class="mdi mdi-tray-arrow-down mr-2"></i>
                {__(
                  'dashboard.sections.massivePaymentLink.import.template.button'
                )}
              </Button>
            </div>
          </Col>
        </Row>

        <Row>
          <Col md='12'>
            <h4 className='import-modal__subtitle'>
              {__('dashboard.sections.massivePaymentLink.import.upload.title')}
            </h4>
            <div className='file-field-dropzone'>
              {isFetching && (
                <div>
                  {__(
                    'dashboard.sections.massivePaymentLink.import.upload.message.uploading'
                  )}
                </div>
              )}
              {!isFetching && (
                <Dropzone
                  className='no-dropzone'
                  activeClassName='dropzoneActive'
                  rejectClassName='dropzoneReject'
                  accept='.csv,text/csv,application/vnd.ms-excel'
                  maxSize={1e6}
                  onDrop={(acceptedFiles, fileRejections) => {
                    const hasRejections = Array.isArray(fileRejections) && fileRejections.length > 0;
                    const firstAccepted = Array.isArray(acceptedFiles) && acceptedFiles.length > 0 ? acceptedFiles[0] : null;
                    
                    if (hasRejections || !firstAccepted || !isCsvFile(firstAccepted)) {
                      setLocalError(__('dashboard.sections.massivePaymentLink.import.upload.invalidType'));
                      return;
                    }
                    
                    setLocalError(null);
                    onUpload(firstAccepted);
                  }}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <Icon path={mdiCloudUploadOutline} size={2} className="mx-auto text-primary" />
                      <p>
                        {__(
                          'dashboard.sections.massivePaymentLink.import.upload.message.drag'
                        )}
                        <br />
                        {__(
                          'dashboard.sections.massivePaymentLink.import.upload.message.click'
                        )}
                      </p>
                      {localError && (
                        <div className="text-danger mt-2">
                          <i className="mdi mdi-alert mr-1" />
                          {localError}
                        </div>
                      )}
                    </div>
                  )}
                </Dropzone>
              )}
            </div>
          </Col>
        </Row>
      </ModalBody>

      <div className='modal-separator'>
        <hr />
      </div>

      <ModalFooter>
        <Button onClick={hideModal}>
          {__('dashboard.sections.massivePaymentLink.modals.buttons.cancel')}
        </Button>
      </ModalFooter>
    </div>
  );
};

export default ImportStep;
